import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, OnDestroy, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  IOption,
  DateRangeFilterOption,
  DateRangeFilterOptions,
  DATE_RANGE_FILTER_OPTIONS_LIST,
  getDatesByRange,
  getDatesBySelectedOption,
  convertDateToRange,
} from '@miq/shared-models';
import { FilterOptionService } from './filter-option.service';
import { rangeTypesList } from '../range-button/range-button.enum';
import { UpperCasePipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatButtonToggleGroup, MatButtonToggle } from '@angular/material/button-toggle';
import { MatTooltip } from '@angular/material/tooltip';
import { MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { OnlyNumbersDirective } from '../../directives/only-numbers.directive';
import { L10nTranslatePipe } from 'angular-l10n';
@Component({
  selector: '[miq-filter-option]',
  templateUrl: './filter-option.component.html',
  styleUrl: './filter-option.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIcon,
    MatMenuTrigger,
    MatMenu,
    MatButtonToggleGroup,
    MatMenuItem,
    FormsModule,
    ReactiveFormsModule,
    MatButtonToggle,
    MatTooltip,
    MatFormField,
    MatInput,
    OnlyNumbersDirective,
    UpperCasePipe,
    L10nTranslatePipe
],
})
export class FilterOptionComponent implements OnInit, OnDestroy {
  form = input<FormGroup>();
  filter = input<IOption>();
  fromRange: FormControl<number | null>;
  toRange: FormControl<number | null>;
  includeEmpty: boolean;
  excludeEmpty: boolean;
  dateFilterOptions: DateRangeFilterOption[] = [];
  filterOptionControl = new FormControl();
  disableDateFilter = false;
  allowedKeyCodes = [189, 173, 109 /*-*/];
  restrictedKeyCodes = [190 /*.*/];
  private readonly optionMenuIcons = {
    default: 'filter-option',
    selected: 'filter-option-green',
  };
  optionMenuIcon = signal(this.optionMenuIcons.default);
  protected destroyRef = inject(DestroyRef);
  private readonly filterOptionService = inject(FilterOptionService);
  ngOnInit() {
    this.setFilterOptionControl();
    this.initDateFilterOptions();
    this.filterOptionService.clearNullFilter$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.filterOptionControl.setValue('default', { emitEvent: false });
      this.includeEmpty = false;
      this.excludeEmpty = false;
      this.optionMenuIcon.set(this.getSvgIcon());
      this.disableFormOnExcludeEmpty();
    });
    this.disableFormOnExcludeEmpty();
    this.convertInputDates();
  }
  private convertInputDates() {
    this.fromRange = new FormControl(convertDateToRange(this.fromControl?.value));
    this.toRange = new FormControl(convertDateToRange(this.toControl?.value));
  }
  private setFilterOptionControl() {
    const { filters } = this.filterOptionService.searchParams;
    this.includeEmpty = filters.some(each => each['key'] === this.filter()['key'] && each.includeEmpty);
    this.excludeEmpty = filters.some(each => each['key'] === this.filter()['key'] && each.excludeEmpty);
    let selectedOption = 'default';
    if (this.includeEmpty) {
      selectedOption = 'includeEmpty';
    } else if (this.excludeEmpty) {
      selectedOption = 'excludeEmpty';
    }
    this.filterOptionControl.setValue(selectedOption);
    this.filterOptionControl.valueChanges.subscribe(value => {
      this.includeEmpty = value === 'includeEmpty';
      this.excludeEmpty = value === 'excludeEmpty';
      const newFilters = this.filterFormState();
      this.filterOptionService.searchQuery(newFilters);
      this.optionMenuIcon.set(this.getSvgIcon());
    });
    this.optionMenuIcon.set(this.getSvgIcon());
  }
  private filterFormState() {
    const { filters } = this.filterOptionService.searchParams;
    filters.forEach(eachFilter => {
      if (
        Array.isArray(this.filter().associations) &&
        this.filter().associations.length > 0 &&
        eachFilter.type === 'group'
      ) {
        return eachFilter.values.forEach(each => this.assignFilterOptions(each));
      }
      this.assignFilterOptions(eachFilter);
    });
    this.disableFormOnExcludeEmpty();
    return filters;
  }
  private assignFilterOptions(eachFilter: IOption) {
    if (eachFilter['key'] === this.filter()['key']) {
      const options =
        this.excludeEmpty && eachFilter.values
          ? { values: null, includeEmpty: this.includeEmpty, excludeEmpty: this.excludeEmpty }
          : { includeEmpty: this.includeEmpty, excludeEmpty: this.excludeEmpty };
      return Object.assign(eachFilter, options);
    } else {
      return eachFilter;
    }
  }
  private disableFormOnExcludeEmpty() {
    if (this.excludeEmpty) {
      this.disableDateFilter = true;
      this.parentGroupControl.reset();
      this.parentGroupControl.disable();
    } else {
      this.disableDateFilter = false;
      this.parentGroupControl.enable();
    }
  }
  get isDateRangeFilter() {
    return (
      rangeTypesList.includes(this.filter().type) && (this.filter().dataType === 'date' || this.filter().inputType === 'date')
    );
  }
  private initDateFilterOptions() {
    if (this.isDateRangeFilter) {
      this.dateFilterOptions = DATE_RANGE_FILTER_OPTIONS_LIST;
    }
  }
  setNumberRangeFilter() {
    if (this.fromRange.value != null && this.toRange.value != null) {
      const { from, to } = getDatesByRange(this.fromRange.value, this.toRange.value);
      this.setFromToValue(from, to);
      this.enumSelectionControl?.setValue(DateRangeFilterOptions.customRange);
    }
  }
  private setFromToValue(from: string, to: string) {
    this.fromControl.setValue(from);
    this.toControl.setValue(to);
  }
  setDateRangeFilter(dateOption: DateRangeFilterOption) {
    if (this.fromControl && this.toControl) {
      const { from, to } = getDatesBySelectedOption(dateOption.key);
      this.setFromToValue(from, to);
      this.convertInputDates();
      this.enumSelectionControl.setValue(dateOption.key);
    }
  }
  get parentGroupControl(): FormGroup | FormControl {
    return (this.form().controls[this.filter().key] || this.form().controls[this.filter().field]) as
      | FormGroup
      | FormControl;
  }
  /* Used only for Date Range Filters  */
  get fromControl(): FormControl {
    return (this.parentGroupControl as FormGroup).controls?.from as FormControl;
  }
  get toControl(): FormControl {
    return (this.parentGroupControl as FormGroup).controls?.to as FormControl;
  }
  get enumSelectionControl(): FormControl {
    return (this.parentGroupControl as FormGroup).controls?.enumSelection as FormControl;
  }
  /**
   * Get the svg icon to show in the filterOptionMenu
   * @returns
   */
  getSvgIcon(): string {
    return ['includeEmpty', 'excludeEmpty'].includes(this.filterOptionControl.value)
      ? this.optionMenuIcons.selected
      : this.optionMenuIcons.default;
  }
  ngOnDestroy(): void {
    // Required by untilDestroyed
  }
}
